import { useAuth } from "@clerk/clerk-react";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { cancelBatchWorkProducts, checkBatchReadiness, generateAllWorkProducts, getBatchWorkProducts, listActiveBatchWorkProducts } from "./api";
import { createBatchGenerationRegistry, type BatchGenerationRegistry } from "./registry";
import type { BatchGenerationController, BatchHandle, BatchReadiness, StartBatchRequest } from "./types";

interface BatchGenerationContextValue extends BatchGenerationController {
    registry: BatchGenerationRegistry;
}

const BatchGenerationContext = createContext<BatchGenerationContextValue | null>(null);
const POLL_INTERVAL_MS = 5_000;

export function BatchGenerationProvider({ children }: { children: ReactNode }) {
    const { getToken } = useAuth();
    const registry = useMemo(() => createBatchGenerationRegistry(), []);

    const token = useCallback(async () => {
        const value = await getToken();
        if (!value) throw new Error("Your session is unavailable. Please sign in again.");
        return value;
    }, [getToken]);

    useEffect(() => {
        let stopped = false;
        let running = false;
        const poll = async () => {
            if (stopped || running) return;
            running = true;
            try {
                const authToken = await token();
                const knownIds = registry.activeBatchIds();
                const [restoredResult, ...knownResults] = await Promise.allSettled([
                    listActiveBatchWorkProducts(authToken),
                    ...knownIds.map(batchId => getBatchWorkProducts(authToken, batchId))
                ]);
                if (stopped) return;
                const byId = new Map<string, BatchHandle>();
                if (restoredResult.status === "fulfilled") {
                    restoredResult.value.forEach(handle => byId.set(handle.batchId, handle));
                }
                knownResults.forEach(result => {
                    if (result.status === "fulfilled") byId.set(result.value.batchId, result.value);
                });
                byId.forEach(handle => registry.syncBatch(handle));
            } catch (error) {
                // Durable status remains in the backend and the next five-second
                // iteration retries; a transient poll must not fail the batch.
                console.warn("Batch generation status poll failed", error);
            } finally {
                running = false;
            }
        };
        void poll();
        const interval = window.setInterval(() => {
            void poll();
        }, POLL_INTERVAL_MS);
        return () => {
            stopped = true;
            window.clearInterval(interval);
        };
    }, [registry, token]);

    const checkReadiness = useCallback(
        async (productIds: number[], options?: { trackChat?: boolean }): Promise<BatchReadiness> => {
            const trackChat = options?.trackChat !== false;
            try {
                const readiness = await checkBatchReadiness(await token(), productIds);
                if (trackChat) registry.setReadiness(readiness);
                return readiness;
            } catch (error) {
                if (trackChat) {
                    registry.failPendingChat(error instanceof Error ? error.message : "Context Mesh check failed.");
                }
                throw error;
            }
        },
        [registry, token]
    );

    const registerBatch = useCallback(
        (handle: BatchHandle) => {
            registry.registerBatch(handle);
        },
        [registry]
    );

    const subscribe = useCallback((listener: Parameters<BatchGenerationRegistry["subscribe"]>[0]) => registry.subscribe(listener), [registry]);

    const startBatch = useCallback(
        async (request: StartBatchRequest): Promise<BatchHandle> => {
            try {
                const handle = await generateAllWorkProducts(await token(), request.productIds, request.origin);
                registerBatch(handle);
                return handle;
            } catch (error) {
                const readiness = (error as Error & { readiness?: BatchReadiness }).readiness;
                if (request.origin === "chat") {
                    if (readiness) registry.setReadiness(readiness);
                    else registry.failPendingChat(error instanceof Error ? error.message : "Batch generation failed to start.");
                }
                throw error;
            }
        },
        [registerBatch, registry, token]
    );

    const cancelBatch = useCallback(
        async (batchId: string): Promise<BatchHandle> => {
            const handle = await cancelBatchWorkProducts(await token(), batchId);
            registry.syncBatch(handle);
            return handle;
        },
        [registry, token]
    );

    const failPendingChat = useCallback((message: string) => registry.failPendingChat(message), [registry]);
    const value = useMemo(
        () => ({ registry, checkReadiness, startBatch, cancelBatch, registerBatch, subscribe, failPendingChat }),
        [registry, checkReadiness, startBatch, cancelBatch, registerBatch, subscribe, failPendingChat]
    );
    return <BatchGenerationContext.Provider value={value}>{children}</BatchGenerationContext.Provider>;
}

export function useBatchGeneration(): BatchGenerationContextValue {
    const value = useContext(BatchGenerationContext);
    if (!value) throw new Error("useBatchGeneration must be used within BatchGenerationProvider");
    return value;
}

export function useOptionalBatchGeneration(): BatchGenerationContextValue | null {
    return useContext(BatchGenerationContext);
}
